/**
 * Achievement badge component displaying level rank (Bronze/Silver/Gold) with star rating
 * Used to visually distinguish between basic, advanced, and elite levels
 */

import React from "react";
import { Box, Text } from "@mantine/core";
import { IconStarFilled } from "@tabler/icons-react";
import { getLevelConfig, type LevelType } from "../utils/levelUtils";

interface LevelBadgeProps {
  level: LevelType;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  customLabel?: string;
}

/**
 * Level badge with gradient background, star rating, and rank label
 */
const LevelBadge: React.FC<LevelBadgeProps> = ({
  level,
  size = "md",
  showLabel = true,
  customLabel,
}) => {
  const config = getLevelConfig(level);

  // Size configurations - More compact
  const sizeConfig = {
    sm: {
      padding: "2px 6px",
      fontSize: "0.65rem",
      starSize: 8,
      gap: 2,
    },
    md: {
      padding: "4px 8px",
      fontSize: "0.75rem",
      starSize: 10,
      gap: 3,
    },
    lg: {
      padding: "6px 12px",
      fontSize: "0.875rem",
      starSize: 12,
      gap: 4,
    },
  };

  const currentSize = sizeConfig[size];

  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: currentSize.gap,
        padding: currentSize.padding,
        borderRadius: "8px",
        background: config.gradient,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Stars */}
      <Box style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: config.stars }).map((_, index) => (
          <IconStarFilled
            key={index}
            size={currentSize.starSize}
            color="#FFD700"
            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}
          />
        ))}
      </Box>

      {/* Badge label */}
      {showLabel && (
        <Text
          size={currentSize.fontSize}
          fw={700}
          c="white"
          style={{
            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
            letterSpacing: "0.5px",
          }}
        >
          {customLabel || config.badgeType}
        </Text>
      )}
    </Box>
  );
};

export default LevelBadge;
